import { mapShopifyProductsToProducts } from '@utils/product';
import { shopifyGraphqlIdToAdminId } from '@utils/shopifyIds';
import type {
  GetStaticPaths,
  GetStaticProps,
  GetStaticPropsContext,
} from 'next';
import { useTranslation } from 'next-i18next';
import React from 'react';

import { ssgClient } from '../../apollo/api/avantArteClient';
import { Provider as ManifoldProvider } from '../../components/Auction/Manifold/Provider';
import MetaTags from '../../components/Content/MetaTags';
import { GlossaryContextProvider } from '../../components/Glossary/GlossaryContext';
import Layout from '../../components/Layout';
import Artist from '../../components/PageTypes/Artist';
import { ArtistSchema } from '../../components/Schema';
import { ARTIST_PAGE_TYPE, ONE_HOUR_IN_SECONDS } from '../../constants';
import { PRODUCT_TAG_COMING_SOON } from '../../constants/product';
import { AvantArteArtistsSearchGateway } from '../../gateways/AvantArteArtistsSearchGateway';
import { imageOgStaticLoader } from '../../helpers/og-image';
import { ArtistPageData } from '../../types/Artists';
import { FranchisesStories } from '../../types/Franchises';
import {
  GeneratedGetDrawByProductIDsQuery,
  GeneratedGetDrawByProductIDsQueryVariables,
  GetDrawByProductIDsDocument,
} from '../../types/generated';
import { ArtistStoryblok } from '../../types/generated-storyblok';
import { getArtistsCount, getRelatedArtists } from '../../utils/artists';
import {
  fetchGlossary,
  mapGlossaryLinks,
  mapGlossaryTerms,
  richTextWithGlossary,
} from '../../utils/glossary';
import { mapFranchiseToItem } from '../../utils/journals/insights';
import { fetchAllProducts } from '../../utils/shopify/fetchAllProducts';
import { getStoryblokPage, useStoryblok } from '../../utils/storyblok';
import { pageTranslations } from '../../utils/translations';

const ARTISTS_ROOT_PATH = 'artists';

export default function Page({
  glossary = [],
  locale,
  locales,
  ogImageUrl,
  pageData,
  preview,
  story,
}: PageProps) {
  const enableBridge = preview;
  story = useStoryblok(story, enableBridge, locale);

  const data = pageData as ArtistPageData;
  const content = {
    ...(story.content as ArtistStoryblok),
    relatedArtists: data.relatedArtists,
    artistsCount: data.artistsCount,
    internalId: data.internalId,
  };

  const { t } = useTranslation(['artists', 'og']);

  return (
    <>
      {!!story.content.excludeFromIndex && <MetaTags robots="noindex" />}
      <Layout
        description={content.seo?.description}
        locale={locale}
        locales={locales}
        ogImageUrl={ogImageUrl}
        title={t('seoTitle', { artist: content.name })}
      >
        <ManifoldProvider>
          <GlossaryContextProvider
            analytics={{
              context: 'artist practice overview',
              page: ARTIST_PAGE_TYPE,
            }}
            glossary={glossary}
          >
            <Artist blok={content} pageData={data} />
          </GlossaryContextProvider>
          <ArtistSchema
            description={content.seo.description}
            fullSlug={story.full_slug}
            image={content.thumbnail?.filename}
            name={content.name}
          />
        </ManifoldProvider>
      </Layout>
    </>
  );
}

export const getStaticProps: GetStaticProps = async (ctx) => {
  const { locale, locales, params, preview = false } = ctx;
  const slug = params?.slug ? (params.slug as string[]).join('/') : 'home';
  const storyblokPath = `cdn/stories/${ARTISTS_ROOT_PATH}/${slug}`;
  const page = await getStoryblokPage(storyblokPath, {
    locale,
    preview,
    resolve_links: 'story',
    resolve_relations: ['exclusive.event.link'],
  });

  if (!page?.data.story) {
    return {
      notFound: true,
    };
  }

  const artistName = page.data.story.name;
  const artistProductsTag = page.data.story.content.shopifyTag
    ? page.data.story.content.shopifyTag
    : artistName;

  const [
    glossary,
    insights,
    internalId,
    artistsCount,
    relatedArtists,
    artistProducts,
  ] = await Promise.all([
    fetchGlossary({ locale, preview }),
    fetchInsights(slug, locale, preview),
    fetchInternalID(ctx, slug),
    getArtistsCount(),
    getRelatedArtists(artistName),
    fetchAllProducts(`tag:'${artistProductsTag}' AND tag:'Listing page'`),
  ]);

  const hideCollaborations = !!page.data.story.content.hideCollaborations;
  const hasUpcomingProductPage =
    artistProducts.some((w) =>
      w.node.tags.some((t) => t === PRODUCT_TAG_COMING_SOON),
    ) ||
    (!artistProducts.length && !hideCollaborations);
  const hasUpcomingProducts =
    hasUpcomingProductPage || !!page.data.story.content.plannedReleases?.length;
  const allArtistWorks: MultiCurrencyProduct[] = mapShopifyProductsToProducts(
    artistProducts.map((product) => product.node),
  );

  const practiceOverview = richTextWithGlossary(
    page.data.story.content.practiceOverview || '',
    glossary,
  );

  const artistGlossaryTerms = glossary.filter((term) => {
    const terms = page.data.story.content.glossary || [];
    return terms.includes(term.uuid);
  });
  const artistProductNodes = (artistProducts || []).map(
    (product) => product.node,
  );

  const artistProductIds = artistProductNodes.map(({ id }) =>
    shopifyGraphqlIdToAdminId(id),
  );
  const draws = artistProductIds.length
    ? await ssgClient(ctx)
        .query<
          GeneratedGetDrawByProductIDsQuery,
          GeneratedGetDrawByProductIDsQueryVariables
        >({
          errorPolicy: 'all',
          query: GetDrawByProductIDsDocument,
          variables: {
            productIDs: artistProductIds,
          },
        })
        .then(({ data }) => data.getDrawByProductIDs)
    : [];

  const pageData: ArtistPageData = {
    artistGlossaryTerms: mapGlossaryLinks(artistGlossaryTerms),
    artistProducts: allArtistWorks,
    insights,
    relatedArtists,
    artistsCount,
    hasUpcomingProducts,
    collaborationsWithArtist: artistProducts.length,
    hasUpcomingProductPage,
    internalId,
    practiceOverview,
    draws,
  };

  const ogImageUrl = await imageOgStaticLoader({
    overlineKey: { key: 'og:artists.artist.overline' },
    titleKey: page.data.story.content.name,
    imageSrc: page.data.story.content.thumbnail?.filename
      ? page.data.story.content.thumbnail.filename
      : '',
    imageWidth: 510,
    imageHeight: 510,
    locale,
  });

  return {
    props: {
      glossary: mapGlossaryTerms(glossary),
      story: page.data.story,
      pageData,
      preview,
      locale,
      locales,
      ogImageUrl,
      ...(await pageTranslations(locale!, ['artists', 'og'])),
    },
    revalidate: ONE_HOUR_IN_SECONDS,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    // generate a sample page during build time to be safe
    paths: [{ params: { slug: ['james-jean'] } }],
    fallback: 'blocking',
  };
};

const fetchInternalID = async (ctx: GetStaticPropsContext, slug: string) => {
  const list = await new AvantArteArtistsSearchGateway(
    ssgClient(ctx),
  ).getArtistsBySlug([slug]);

  return list?.[0]?.id || null;
};

const fetchInsights = async (
  slug: string,
  locale?: string,
  preview?: boolean,
) => {
  const insights: FranchisesStories | null = await getStoryblokPage(
    'cdn/stories',
    {
      locale,
      preview,
      search_term: `${ARTISTS_ROOT_PATH}/${slug}`,
      starts_with: 'insights/',
    },
  );

  return (insights?.data.stories || []).map(mapFranchiseToItem);
};
